import { isMobile, smallerThan } from "@global/Composables/useDevice";
import { usePage } from "@inertiajs/vue3";
import { useSessionStorage } from "@vueuse/core";
import type { Map } from "mapbox-gl";
import { defineStore } from "pinia";
import { computed, ref, watch, type ComputedRef } from "vue";
import { getActiveCountriesBBox, updateMapPartial, updateMapReplace } from "./utils";

const isSmallerThanXl = smallerThan("xl");
const isSmallerThanLg = smallerThan("lg");
const isSmallerThanMd = smallerThan("md");

type VisibilityProps = "map" | "list" | "list_map_split" | "drawer_open" | "drawer_closed";

export const useOverviewStore = defineStore(
    "overviewStore",
    () => {
        const page = usePage();

        // Search parameters from the page props
        const search = computed(() => page.props?.search ?? null) as ComputedRef<Domain.Overview.DataTransferObjects.SearchDTO | null>;

        // Mapbox map instance
        const map = ref<Map>();
        const setMap = (val: Map) => (map.value = val);

        // Map markers and bidder cards
        const markers = ref<App.Http.Main.Overview.ViewModels.MapMarker[]>([]);
        const setMarkers = (data: App.Http.Main.Overview.ViewModels.MapMarker[]) => (markers.value = data);
        const cards = ref<App.Data.Main.Overview.PaginatedBidderCardListData>();
        const setCards = (data: App.Data.Main.Overview.PaginatedBidderCardListData) => (cards.value = data);

        // Map visibility state and related properties
        const getDefaultVisibilityState = () => {
            if (isMobile.value) return "drawer_open";
            if (isSmallerThanXl.value) return "list";
            return "list_map_split";
        };
        const visibilityState = useSessionStorage<VisibilityProps>("visibilityState", getDefaultVisibilityState());
        const isVisibilityStateList = computed(() => ["list", "list_map_split"].includes(visibilityState.value));
        const isVisibilityStateDrawer = computed(() => ["drawer_open", "drawer_closed"].includes(visibilityState.value));
        const setVisibilityState = (state?: VisibilityProps) => (visibilityState.value = state ? state : getDefaultVisibilityState());

        // A list of country codes representing the countries currently displayed on the map
        const activeCountriesBBox = ref<[number, number, number, number]>([0, 0, 0, 0]);
        const setActiveCountriesBBox = (map: Map) => (activeCountriesBBox.value = getActiveCountriesBBox(map));

        // Drawer properties
        const drawerHandleHeight = 70;
        const drawerMapOffset = computed(() => (isVisibilityStateDrawer.value ? 50 : 0));

        // Overview page state
        const isOverviewPageActive = computed(() => page.component === "Overview/Index");
        const hasOverviewPageMounted = ref(false);
        const setHasOverviewPageMounted = () => (hasOverviewPageMounted.value = true);

        // Loading state
        const isProcessingMap = ref<boolean>(false);
        const setIsProcessingMap = (val: boolean) => (isProcessingMap.value = val);
        // Watch the overview page state and set the processing state to false when the page is not active
        // Prevents the loading state from being stuck when navigating away from the overview page
        watch(isOverviewPageActive, (newVal, oldVal) => !newVal && oldVal && setIsProcessingMap(false));

        // Paginator visibility state
        const isPaginatorInView = ref(false);
        const setIsPaginatorInView = (val: boolean) => (isPaginatorInView.value = val);

        // Hovered marker and bidder card state
        const hoveredMarkerId = ref<number | null>(null);
        const hoveredBidderCardId = ref<number | null>(null);
        const setHoveredMarkerId = (val: number | null) => (hoveredMarkerId.value = val);
        const setHoveredBidderCardId = (val: number | null) => (hoveredBidderCardId.value = val);

        // Active bidder card popup state
        const activeBidderCardPopupId = ref<number | null>(null);
        const setActiveBidderCardPopupId = (val: number | null) => (activeBidderCardPopupId.value = val);

        // Map canvas top offset
        const mapOffsetTop = computed(() => {
            if (isSmallerThanMd.value) return 138;
            if (isSmallerThanLg.value) return 150;
            return 160;
        });

        return {
            search,
            activeBidderCardPopupId,
            cards,
            drawerHandleHeight,
            drawerMapOffset,
            hasOverviewPageMounted,
            hoveredBidderCardId,
            hoveredMarkerId,
            isProcessingMap,
            isOverviewPageActive,
            isPaginatorInView,
            isVisibilityStateDrawer,
            isVisibilityStateList,
            map,
            mapOffsetTop,
            markers,
            visibilityState,
            activeCountriesBBox,
            setActiveCountriesBBox,
            setActiveBidderCardPopupId,
            setHasOverviewPageMounted,
            setHoveredBidderCardId,
            setHoveredMarkerId,
            setIsProcessingMap,
            setIsPaginatorInView,
            setMap,
            setVisibilityState,
            setMarkers,
            setCards,
            updateMapPartial,
            updateMapReplace,
        };
    },
    { persist: false },
);
